/**
 * Reading right-panel state out of a URL, once.
 *
 * The `?rightPanel=` contract used to *be* the state. It is now input only: a
 * shared or bookmarked link seeds the store when a thread mounts, and nothing
 * writes these keys again. That is the same treatment `diffRouteSearch` already
 * gave the boolean flags that preceded it — "accepted on the way IN so shared
 * bookmarks keep working, never written back OUT".
 *
 * Doing it this way rather than mirroring the store into the URL is deliberate:
 * two live representations of one thing can disagree, and reconciling them is
 * exactly the drift this port set out to remove.
 *
 * Returning `null` means "this link says nothing about the panel" — which is
 * different from "open an empty panel", and must stay different, or mounting a
 * thread from an ordinary link would stomp the layout the store just restored.
 */
import { TurnId } from "@cmmd-forge/contracts";

import {
  RIGHT_PANEL_KINDS,
  browserSurface,
  singletonSurface,
  type RightPanelKind,
  type RightPanelSurface,
  type ThreadRightPanelState,
} from "./rightPanelSurface";

function isRightPanelKind(value: unknown): value is RightPanelKind {
  return typeof value === "string" && (RIGHT_PANEL_KINDS as readonly string[]).includes(value);
}

function readText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isLegacyOpenValue(value: unknown): boolean {
  return value === "1" || value === 1 || value === true;
}

function isLegacyClosedValue(value: unknown): boolean {
  return value === "0" || value === 0 || value === false;
}

/**
 * The pre-consolidation boolean flags (`?diff=1`), resolved in the priority the
 * renderer of that era used: canvas > preview > diff. The order is not
 * arbitrary — changing it would send a link that works today to a different
 * surface than it opens today.
 */
function inferLegacyBooleanKind(search: Record<string, unknown>): RightPanelKind | null {
  if (isLegacyOpenValue(search["canvas"])) return "canvas";
  if (isLegacyOpenValue(search["preview"])) return "preview";
  if (isLegacyOpenValue(search["diff"])) return "diff";
  return null;
}

function parseTabList(value: unknown): RightPanelKind[] {
  const raw = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];
  const seen = new Set<RightPanelKind>();
  for (const entry of raw) {
    const trimmed = typeof entry === "string" ? entry.trim() : entry;
    // An old link naming a surface this build removed must not wedge the
    // panel, so unknown ids are dropped rather than carried.
    if (isRightPanelKind(trimmed)) seen.add(trimmed);
  }
  return [...seen];
}

/**
 * A file surface is addressed by path, which the old contract had no key for,
 * so no legacy link can ask for one. That is expressed in the parameter type
 * rather than as a branch — an unreachable `if (kind === "file")` would be a
 * claim about behaviour that can never run.
 */
type LegacyLinkableKind = Exclude<RightPanelKind, "file" | "pull-request">;

function surfaceForKind(
  kind: LegacyLinkableKind,
  search: Record<string, unknown>,
): RightPanelSurface {
  if (kind === "preview") {
    return browserSurface(readText(search["previewTabId"]));
  }
  if (kind === "diff") {
    const turnIdRaw = readText(search["diffTurnId"]);
    const turnId = turnIdRaw ? TurnId.make(turnIdRaw) : null;
    return {
      id: "diff",
      kind: "diff",
      turnId,
      // A file with no turn addresses nothing.
      filePath: turnId ? readText(search["diffFilePath"]) : null,
    };
  }
  return singletonSurface(kind);
}

export function rightPanelStateFromLegacySearch(
  search: Record<string, unknown>,
): ThreadRightPanelState | null {
  const named = isRightPanelKind(search["rightPanel"])
    ? (search["rightPanel"] as RightPanelKind)
    : inferLegacyBooleanKind(search);
  const tabKinds = parseTabList(search["rightPanelTabs"]);

  const kinds = named && !tabKinds.includes(named) ? [...tabKinds, named] : tabKinds;
  const orderedKinds = kinds.filter(
    (kind): kind is LegacyLinkableKind => kind !== "file" && kind !== "pull-request",
  );
  if (orderedKinds.length === 0) return null;

  const surfaces = orderedKinds.map((kind) => surfaceForKind(kind, search));
  const activeSurfaceId =
    (named ? surfaces.find((surface) => surface.kind === named)?.id : undefined) ??
    surfaces[0]?.id ??
    null;

  return {
    // An explicit `rightPanelOpen=0` collapses the panel but keeps its tabs,
    // which is how the old contract expressed a remembered-but-hidden panel.
    isOpen: !isLegacyClosedValue(search["rightPanelOpen"]),
    activeSurfaceId,
    surfaces,
  };
}
